import { AppContext, usePermission } from "@ctsu/loader";
import { useContext, useEffect } from "react";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import csmKeys from "assets/text/csmKeys";
import { GetDecodedQueryParams } from "helpers/utils";
import { compose, filter, head, includes, is, lensProp, prop, sortBy, view } from "ramda";
import { useSelector } from "react-redux";

const CLINICAL_TRIAL_PROJECTS = ["CTIMS", "CTMS", "CTIS"];

export default function useGetFromAppConfig(a, b) {
  const { application, session } = useContext(AppContext);
  let config = [];

  try {
    if (session) {
      const { appConfig } = application;
      const propOne = lensProp(a);
      const propTwo = lensProp(b);
      const child = JSON.parse(view(propOne, appConfig));
      config = sortBy(prop("label"))(view(propTwo, child));
    }
  } catch (e) {
    console.warn(e);
  }

  return config;
}
export function useCurrentUser() {
  const context = useContext(AppContext);
  const { session } = context;
  let userFirstLast = "";
  if (session) {
    userFirstLast = session.userFirstLast;
  }
  return userFirstLast;
}

export const useUserAffliations = () => {
  const context = useContext(AppContext);

  const { session } = context;
  if (session && session.userGroups) {
    const { userGroups = [] } = session || [];
    return userGroups.filter(g => CLINICAL_TRIAL_PROJECTS.indexOf(g) < 0);
  }
  return [];
};
export function useGetUserGroup() {
  const userAffiliations = useUserAffliations();
  const updateUserGroup = useSelector(state => state.updatedUserGroup.data);
  const query = GetDecodedQueryParams();
  const userGroupFromParams = query.get("userGroup");
  if (userGroupFromParams) {
    return userGroupFromParams;
  }

  if (updateUserGroup && updateUserGroup.length > 1) {
    return updateUserGroup;
  }
  if (userAffiliations.indexOf("CTSU") !== -1) {
    return "CTSU";
  }
  if (userAffiliations.indexOf("DCTD") !== -1) {
    return "DCTD";
  }
  if (userAffiliations.indexOf("COG") !== -1 || userAffiliations.indexOf("PEP-CTN") !== -1) {
    return "COG";
  }
  return head(userAffiliations.sort() || "");
}
export function useGetAlphaIds() {
  let idsMap = new Map();
  const alphaId = useGetFromAppConfig("LOV_INSTIT_NONNUMERIC_CTEP_ID_LIST", "institnonnumericCtepId");
  if (alphaId && is(Array, alphaId)) {
    idsMap = new Map(alphaId.map(ids => [ids, ids]));
  }
  return idsMap;
}

export const useGetUserName = () => {
  const context = useContext(AppContext);
  const { session } = context;
  let username = "";
  if (session) {
    username = session.username;
  }
  return username;
};

export const useHasSuperUserRoles = () => {
  const context = useContext(AppContext);
  const { session } = context;
  if (session) {
    const { userPermissions } = session;
    return compose(filter(includes("rostering.welcome.group.edit")))(userPermissions);
  }
  return false;
};

export const useRosterPermission = permission => usePermission(permission);

export const useTabSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tabIndex = parseInt(searchParams.get("dGFiSW5kZXg=")) || 0;

  const handleSelect = (index) => {
    searchParams.set("dGFiSW5kZXg=", index);
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  return { tabIndex, handleSelect };
};

export function useRemoveURLParams() {
  useEffect(() => {
    const removeURLParams = () => {
      let url = window.location.href;
      if (url.indexOf("?") !== -1) {
        url = url.split("?")[0];
        window.history.replaceState({}, document.title, url);
      }
    };
    removeURLParams();
  }, []);
}
export function removeURLParams() {
  let url = window.location.href;
  if (url.indexOf("?") !== -1) {
    url = url.split("?")[0];
    window.history.replaceState({}, document.title, url);
  }
}

export function getTabSelection() {
  const searchParams = new URLSearchParams(window.location.search);
  const tabIndex = parseInt(searchParams.get("dGFiSW5kZXg=")) || 0;

  return { tabIndex };
}

export const useGetOneOfAdminPermissions = () => {
  const context = useContext(AppContext);
  const { session } = context;
  if (session) {
    const { userPermissions } = session;
    const requiredPermissions = [
      csmKeys.ROSTERING_UNREGISTERED_SITES_EDIT,
      csmKeys.ROSTERING_LAPS_GRANTS_ADMIN_VIEW,
      csmKeys.ROSTERING_COMMITTEES_SETUP_VIEW,
      csmKeys.ROSTERING_ATTRIBUTE_SETUP_VIEW,
      csmKeys.ROSTERING_ROLES_SETUP_VIEW,
      csmKeys.ROSTERING_APPROVAL_VIEW,
      csmKeys.ROSTERING_RUMS_VIEW
    ];

    for (let permission of requiredPermissions) {
      if (userPermissions.includes(permission)) {
        return permission;
      }
    }

    return csmKeys.ROSTERING_RUMS_VIEW;
  }
  return false;
};
